/**
 * Utilities for working with CDL colors in JavaScript/TypeScript
 *
 * Uses design tokens as the source of truth, with runtime CSS variable
 * extraction for theme-aware color values.
 */

import darkModeTokens from "../tokens/mode.dark.tokens.json";
import lightModeTokens from "../tokens/mode.light.tokens.json";
import paletteTokens from "../tokens/palettes.colors.tokens.json";

/**
 * Resolves a token reference (e.g., "{green.400}") to its actual hex value
 */
function resolveTokenReference(reference: string, palette: any): string {
  // Remove curly braces and split by dot
  const path = reference.replace(/[{}]/g, "").split(".");

  let value = palette;
  for (const key of path) {
    value = value[key];
    if (!value) return reference; // Return original if not found
  }

  return value.$value || value;
}

/**
 * Gets color values from mode tokens, resolving palette references
 */
function getColorsFromTokens(modeTokens: any) {
  return {
    // Chart colors
    chartGreenBase: resolveTokenReference(
      modeTokens.chart.green.base.$value,
      paletteTokens
    ),
    chartRedBase: resolveTokenReference(
      modeTokens.chart.red.base.$value,
      paletteTokens
    ),
    chartYellowBase: resolveTokenReference(
      modeTokens.chart.yellow.base.$value,
      paletteTokens
    ),
    chartOrangeBase: resolveTokenReference(
      modeTokens.chart.orange.base.$value,
      paletteTokens
    ),

    // Accent colors
    accentBase: resolveTokenReference(
      modeTokens.accent.bg.base.$value,
      paletteTokens
    ),

    // Alert colors
    alertBase: resolveTokenReference(
      modeTokens.alert.bg.base.$value,
      paletteTokens
    ),

    // Positive colors
    positiveBase: resolveTokenReference(
      modeTokens.positive.bg.base.$value,
      paletteTokens
    ),

    // Foreground colors
    fgBase: resolveTokenReference(modeTokens.fg.base.$value, paletteTokens),
    fgMuted: resolveTokenReference(modeTokens.fg.muted.$value, paletteTokens),

    // Background colors
    bgBase: resolveTokenReference(modeTokens.bg.base.$value, paletteTokens),
    bgSubtle: resolveTokenReference(modeTokens.bg.subtle.$value, paletteTokens),
  };
}

// Pre-resolved color values from tokens
export const CDL_LIGHT_COLORS = getColorsFromTokens(lightModeTokens);
export const CDL_DARK_COLORS = getColorsFromTokens(darkModeTokens);

/**
 * Gets the computed value of a CSS variable from the document root
 * This will return the actual color value based on the current theme
 *
 * @param variableName - CSS variable name (e.g., '--cdl-chart-green')
 * @returns The computed color value (e.g., '#3BCDAA' in light mode, '#148D74' in dark mode)
 */
export function getCSSVariableValue(variableName: string): string {
  if (typeof window === "undefined") {
    // Server-side: return light mode value from tokens
    console.warn(`getCSSVariableValue called server-side for ${variableName}`);
    return "";
  }

  const styles = getComputedStyle(document.documentElement);
  const value = styles.getPropertyValue(variableName).trim();

  // Handle light-dark() function if the browser doesn't support it
  if (value.startsWith("light-dark(")) {
    const match = value.match(/light-dark\((.*?),\s*(.*?)\)/);
    if (match) {
      const [, lightValue, darkValue] = match;
      const isDark =
        document.documentElement.getAttribute("data-theme") === "dark" ||
        document.documentElement.classList.contains("dark");
      return isDark ? darkValue : lightValue;
    }
  }

  return value;
}

/**
 * Gets CDL color values for use in JavaScript
 * These will automatically use the correct value based on the current theme
 */
export const getCDLColors = () => {
  if (typeof window === "undefined") {
    // Server-side: return light mode colors from tokens
    return CDL_LIGHT_COLORS;
  }

  // Try to get from CSS variables first (respects current theme)
  const fromCSS = {
    chartGreenBase: getCSSVariableValue("--cdl-chart-green-base"),
    chartRedBase: getCSSVariableValue("--cdl-chart-red-base"),
    chartYellowBase: getCSSVariableValue("--cdl-chart-yellow-base"),
    chartOrangeBase: getCSSVariableValue("--cdl-chart-orange-base"),
    accentBase: getCSSVariableValue("--cdl-accent-bg-base"),
    alertBase: getCSSVariableValue("--cdl-alert-bg-base"),
    positiveBase: getCSSVariableValue("--cdl-positive-bg-base"),
    fgBase: getCSSVariableValue("--cdl-fg-base"),
    fgMuted: getCSSVariableValue("--cdl-fg-muted"),
    bgBase: getCSSVariableValue("--cdl-bg-base"),
    bgSubtle: getCSSVariableValue("--cdl-bg-subtle"),
  };

  // Fall back to token values if CSS variables aren't available
  const hasValues = Object.values(fromCSS).every((v) => v && v !== "");
  if (!hasValues) {
    const isDark =
      document.documentElement.getAttribute("data-theme") === "dark" ||
      document.documentElement.classList.contains("dark");
    return isDark ? CDL_DARK_COLORS : CDL_LIGHT_COLORS;
  }

  return fromCSS;
};

/**
 * Hook to get CDL colors that updates when theme changes
 */
export function useCDLColors() {
  const [colors, setColors] = React.useState(getCDLColors);

  React.useEffect(() => {
    const updateColors = () => setColors(getCDLColors());

    // Listen for theme changes
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "class"],
    });

    // Initial update
    updateColors();

    return () => observer.disconnect();
  }, []);

  return colors;
}

// Import React only if needed
import React from "react";
